import {memberRoleActsForTrustee} from '@sollapay/domain/rules';
import {TrustAccountMemberRole} from '@sollapay/enums';
import {useTranslation} from 'react-i18next';

import {PersonCell} from '@/components';
import {
  canHoldRole,
  memberDisplayName,
  otherMemberRole,
  toTeamMember
} from '@/features/trust-accounts/members';

import {PowerOfAttorneyConfirm} from './PowerOfAttorneyConfirm';
import {ShareCurrentUserLabel} from './ShareCurrentUserLabel';
import {ShareRoleMenu} from './ShareRoleMenu';
import {ShareRowConfirm} from './ShareRowConfirm';
import {useShareRowStep} from '../hooks';

import type {PowerOfAttorneyGrantor} from './SharePowerOfAttorneyConsent';
import type {ShareRoleMenuOption} from './ShareRoleMenu';
import type {TrustAccountMemberDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

export interface ShareMemberRowProps {
  member: TrustAccountMemberDTO;
  grantor: PowerOfAttorneyGrantor;
  readOnly: boolean;
  isCurrentUser?: boolean;
  onMove: (member: TrustAccountMemberDTO, role: TrustAccountMemberRole) => Promise<boolean>;
  onRemove: (member: TrustAccountMemberDTO) => Promise<boolean>;
}

/** A firm member with their role; moving to operator (power of attorney) and removing are confirmed in the row. */
export const ShareMemberRow: FC<ShareMemberRowProps> = ({
  member,
  grantor,
  readOnly,
  isCurrentUser = false,
  onMove,
  onRemove
}) => {
  const {t} = useTranslation('trustAccounts');
  const {step, setStep, isBusy, settle} = useShareRowStep<'move' | 'remove'>();
  const name = memberDisplayName(member);
  const moveTo = otherMemberRole(member.role);

  // With two roles, any pick from the menu is a move to the other one.
  const changeRole = () => {
    if (memberRoleActsForTrustee(moveTo)) setStep('move');
    else void settle(() => onMove(member, moveTo));
  };

  return (
    <li data-slot="share-member-row" className="flex flex-col gap-2 py-2">
      <div className="flex items-center gap-3">
        <ShareMemberIdentity member={member} isCurrentUser={isCurrentUser} />
        <ShareRoleMenu
          label={t(`share.roles.${member.role}.label`)}
          personName={name}
          value={member.role}
          options={getMemberRoleOptions(member, t)}
          actions={[
            {label: t('members.card.remove'), onClick: () => setStep('remove'), destructive: true}
          ]}
          readOnly={readOnly}
          onValueChange={changeRole}
        />
      </div>
      {step && (
        <ShareMemberRowStep
          step={step}
          member={member}
          grantor={grantor}
          isBusy={isBusy}
          onConfirm={() =>
            void settle(() => (step === 'move' ? onMove(member, moveTo) : onRemove(member)))
          }
          onCancel={() => setStep(null)}
        />
      )}
    </li>
  );
};

const ShareMemberIdentity: FC<{member: TrustAccountMemberDTO; isCurrentUser: boolean}> = ({
  member,
  isCurrentUser
}) => {
  const {t} = useTranslation('common');

  return (
    <div className="min-w-0 flex-1">
      <PersonCell
        name={memberDisplayName(member)}
        nameEnd={isCurrentUser && <ShareCurrentUserLabel />}
        subtitle={t(`roles.${member.firmRoleKey}`, {defaultValue: member.firmRoleKey})}
        avatarUrl={member.picture}
        seed={member.userId}
        emptyLabel={member.email}
      />
    </div>
  );
};

interface ShareMemberRowStepProps {
  step: 'move' | 'remove';
  member: TrustAccountMemberDTO;
  grantor: PowerOfAttorneyGrantor;
  isBusy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ShareMemberRowStep: FC<ShareMemberRowStepProps> = ({step, member, grantor, ...confirm}) => {
  const {t} = useTranslation('trustAccounts');

  if (step === 'move') {
    return <PowerOfAttorneyConfirm person={member} grantor={grantor} {...confirm} />;
  }
  return (
    <ShareRowConfirm
      message={t('share.member.confirmRemove', {name: memberDisplayName(member)})}
      confirmLabel={t('share.member.confirmRemoveCta')}
      backLabel={t('share.member.back')}
      {...confirm}
    />
  );
};

/** Both roles; one the member's firm role can't hold is disabled and says why. */
function getMemberRoleOptions(member: TrustAccountMemberDTO, t: TFunction): ShareRoleMenuOption[] {
  const teamMember = toTeamMember(member);
  return Object.values(TrustAccountMemberRole).map(role => {
    const canHold = canHoldRole(teamMember, role);
    return {
      value: role,
      label: t(`share.roles.${role}.label`),
      description: t(`share.roles.${role}.${canHold ? 'description' : 'ineligible'}`),
      disabled: !canHold
    };
  });
}
